// Call-attempt identity for the instance-aware call protocol (calling reliability plan v8 §4,
// Release 2 client side). Web counterpart of synexos-mobile src/services/calling/callIdentity.js.
//
// One identity per call attempt, held PER TAB (module state):
//   clientCallId    - generated for every outgoing attempt (`cc_<random>`); idempotency key
//   callInstanceId  - the backend's authoritative id (`call_offer_ack`, incoming offer)
//   version         - last applied `call_state_sync` version for that instance
//
// Compatibility: with CALL_INSTANCE_V1 off the backend sends no instance fields, so every helper
// degrades to the legacy behaviour. Ids are only ever ADDED to outgoing payloads, events without
// ids are never filtered as foreign, and only instance-bound signals are ever queued.

export const HEARTBEAT_INTERVAL_MS = 20000; // v8 §7.1 L2: call_heartbeat every 20 s
export const TERMINAL_STATES = ["ENDED", "REJECTED", "CANCELLED", "MISSED", "BUSY"];
export const QUEUE_TTL_MS = 30000; // a queued signal older than this is dropped (backend timers resolved it)
const QUEUE_TYPES = ["call_answer", "call_reject", "call_busy", "call_end"];
const QUEUE_MAX = 10;
const LIVE_STATES = ["calling", "incoming", "connected"];

const randomHex = (len) => {
  let out = "";
  for (let i = 0; i < len; i += 1) out += Math.floor(Math.random() * 16).toString(16);
  return out;
};

export const newClientCallId = () => `cc_${Date.now().toString(36)}_${randomHex(16)}`;

export const emptyIdentity = () => ({ clientCallId: null, callInstanceId: null, version: 0, ringDeadlineAt: null });

/** A server-issued ring deadline (epoch ms; FCM / storage may carry it as a string), or null. */
export const toDeadline = (value) => {
  if (value === null || value === undefined || value === "") return null;
  const n = Number(value);
  return Number.isFinite(n) && n > 0 ? n : null;
};

/**
 * Outgoing ring: time left until the backend's ring deadline of the current attempt (instance-aware
 * calls), so the caller does not give up before the callee's ring window ends. 0 when unknown,
 * passed, or implausibly far away (clock skew): the caller's own timeout applies then, and the
 * backend's expiry still ends the attempt authoritatively.
 */
export const remainingRingMs = (ident, now = Date.now(), maxMs = 60000) => {
  const deadline = toDeadline(ident && ident.ringDeadlineAt);
  if (!deadline || !(ident && ident.callInstanceId)) return 0;
  const left = deadline - now;
  return left > 0 && left <= maxMs ? left : 0;
};

/**
 * Instance fields to add to an outgoing call event (only those that are known). The server-issued
 * `ring_deadline_at` is echoed with the instance: after a backend restart it is the evidence that
 * the call predates the new process (F-05), so the peer can still be told about a hang-up.
 */
export const instanceFields = (identity) => {
  const out = {};
  if (identity && identity.callInstanceId) {
    out.call_instance_id = identity.callInstanceId;
    if (toDeadline(identity.ringDeadlineAt)) out.ring_deadline_at = toDeadline(identity.ringDeadlineAt);
  }
  if (identity && identity.clientCallId) out.client_call_id = identity.clientCallId;
  return out;
};

/** `{ call_instance_id, ring_deadline_at }` of an incoming ringing call, or {} for a legacy offer. */
export const incomingFields = (incoming) => {
  if (!incoming || !incoming.call_instance_id) return {};
  const out = { call_instance_id: incoming.call_instance_id };
  if (toDeadline(incoming.ring_deadline_at)) out.ring_deadline_at = toDeadline(incoming.ring_deadline_at);
  return out;
};

/**
 * True when `event` explicitly belongs to a DIFFERENT call attempt than `identity` (by instance
 * id, or by client_call_id when the instance is not known yet). Events without ids are never
 * foreign.
 */
export const isForOtherAttempt = (event, identity) => {
  if (!event || !identity) return false;
  const evCi = event.call_instance_id;
  if (evCi && identity.callInstanceId && String(evCi) !== String(identity.callInstanceId)) return true;
  const evCc = event.client_call_id;
  if (evCc && identity.clientCallId && String(evCc) !== String(identity.clientCallId)) return true;
  return false;
};

/** An incoming ringing call vs an event: foreign only if both carry differing instance ids. */
export const isForOtherIncoming = (event, incoming) => {
  if (!event || !incoming) return false;
  const evCi = event.call_instance_id;
  const incCi = incoming.call_instance_id;
  return Boolean(evCi && incCi && String(evCi) !== String(incCi));
};

/**
 * `call_offer_ack` for the current outgoing attempt: null when it is not for this attempt, else
 * { callInstanceId, outcome } with outcome 'ringing' | 'busy' | 'ended' | 'answered'.
 */
export const interpretOfferAck = (ack, identity) => {
  if (!ack || !identity || !identity.clientCallId) return null;
  if (!ack.client_call_id || String(ack.client_call_id) !== String(identity.clientCallId)) return null;
  const status = String(ack.status || "");
  let outcome = "ended";
  if (status === "ringing") outcome = "ringing";
  else if (status === "answered") outcome = "answered";
  else if (status === "busy" || status === "conflict" || status === "rejected") outcome = "busy";
  return { callInstanceId: ack.call_instance_id || null, ringDeadlineAt: toDeadline(ack.ring_deadline_at), outcome };
};

/**
 * What a `call_state_sync` means for the current attempt: { apply: false } for a foreign or
 * stale (older / equal version) sync, else { apply: true, state, terminal, version }.
 */
export const interpretStateSync = (sync, identity) => {
  if (!sync || !sync.call_instance_id || !identity) return { apply: false };
  const matchesInstance =
    identity.callInstanceId && String(sync.call_instance_id) === String(identity.callInstanceId);
  const matchesClient =
    !identity.callInstanceId &&
    identity.clientCallId &&
    sync.client_call_id &&
    String(sync.client_call_id) === String(identity.clientCallId);
  if (!matchesInstance && !matchesClient) return { apply: false };
  const version = Number(sync.version || 0);
  if (version <= Number(identity.version || 0)) return { apply: false };
  const state = String(sync.state || "");
  return { apply: true, state, terminal: TERMINAL_STATES.includes(state), version };
};

/** User notice for a terminal backend state (mirrors the legacy call_reject / call_busy texts). */
export const terminalNotice = (state) => {
  if (state === "REJECTED") return "Call declined";
  if (state === "BUSY") return "User is already on another call";
  return "";
};

const toHeartbeatLiveKitState = (livekitState, callState) => {
  if (livekitState === "reconnecting" || livekitState === "signalReconnecting") return "reconnecting";
  if (livekitState === "connected") return "connected";
  if (livekitState) return "disconnected";
  return callState === "connected" ? "connected" : "disconnected";
};

/** `call_heartbeat` payload (v8 §7.1 L2) or null when there is no instance yet. */
export const heartbeatPayload = (identity, callState, livekitState) => {
  if (!identity || !identity.callInstanceId) return null;
  return {
    type: "call_heartbeat",
    call_instance_id: identity.callInstanceId,
    client_call_state: callState || null,
    livekit_state: toHeartbeatLiveKitState(livekitState, callState),
    ts: Date.now(),
  };
};

/**
 * A LiveKit disconnect that should end the call for both sides (v8 Release 2), except
 * DUPLICATE_IDENTITY (livekit-client DisconnectReason 2: the same identity joined elsewhere).
 */
export const isTerminalLiveKitDisconnect = (reason) => {
  if (reason === undefined || reason === null) return true;
  const text = String(reason).toUpperCase();
  return !(text === "DUPLICATE_IDENTITY" || text === "2");
};

// ── Per-tab identity of the current 1:1 call attempt ────────────────────────────────────────
let identity = emptyIdentity();
let heartbeatTimer = null;
let pending = [];

export const getCallIdentity = () => identity;
export const currentInstanceFields = () => instanceFields(identity);

export function stopCallHeartbeat() {
  if (heartbeatTimer) {
    clearInterval(heartbeatTimer);
    heartbeatTimer = null;
  }
}

/** New outgoing attempt: fresh identity with a new client_call_id (returned). */
export function beginOutgoingAttempt() {
  stopCallHeartbeat();
  identity = { ...emptyIdentity(), clientCallId: newClientCallId() };
  return identity.clientCallId;
}

/** Answering an incoming call: the attempt is the offer's instance (none on a legacy backend). */
export function beginIncomingAttempt(incoming) {
  stopCallHeartbeat();
  identity = {
    ...emptyIdentity(),
    callInstanceId: (incoming && incoming.call_instance_id) || null,
    ringDeadlineAt: incoming && incoming.call_instance_id ? toDeadline(incoming.ring_deadline_at) : null,
  };
}

export function adoptCallInstance(callInstanceId, ringDeadlineAt = null) {
  if (!callInstanceId) return;
  identity = {
    ...identity,
    callInstanceId: String(callInstanceId),
    ringDeadlineAt: identity.ringDeadlineAt || toDeadline(ringDeadlineAt),
  };
}

export function resetCallIdentity() {
  stopCallHeartbeat();
  identity = emptyIdentity();
}

/**
 * Start (or restart) the call_heartbeat for the current instance. `send(payload)` delivers it;
 * `getStatus()` returns { callState, livekitState }. No-op without an instance.
 */
export function startCallHeartbeat(send, getStatus) {
  stopCallHeartbeat();
  if (!identity.callInstanceId || typeof send !== "function") return false;
  const beat = () => {
    let status = {};
    try {
      status = (getStatus && getStatus()) || {};
    } catch (_) {
      status = {};
    }
    const payload = heartbeatPayload(identity, status.callState, status.livekitState);
    if (payload) {
      try {
        send(payload);
      } catch (_) {
        /* a failed beat is retried on the next tick */
      }
    }
  };
  beat();
  heartbeatTimer = setInterval(beat, HEARTBEAT_INTERVAL_MS);
  return true;
}

export const isCallHeartbeatRunning = () => heartbeatTimer !== null;

// ── Outbound queue (WebSocket down) ─────────────────────────────────────────────────────────
// Only INSTANCE-BOUND answer / reject / busy / end are queued: the backend applies them to that
// exact instance or ignores them (duplicate / stale), so a delayed signal can never affect a
// newer call. Legacy signals (no call_instance_id) keep the previous drop-when-offline behaviour.

/** Queue `payload` for the next socket open. Returns true when it was queued. */
export function queueCallSignal(payload, now = Date.now()) {
  if (!payload || !QUEUE_TYPES.includes(payload.type) || !payload.call_instance_id) return false;
  pending = pending.filter(
    (p) => !(p.payload.type === payload.type && p.payload.call_instance_id === payload.call_instance_id)
  );
  pending.push({ payload, at: now });
  if (pending.length > QUEUE_MAX) pending = pending.slice(-QUEUE_MAX);
  return true;
}

/** Send queued signals in order (expired ones are dropped). Returns the number sent. */
export function flushQueuedCallSignals(send, now = Date.now()) {
  if (typeof send !== "function" || pending.length === 0) return 0;
  const items = pending;
  pending = [];
  let sent = 0;
  items.forEach((item) => {
    if (now - item.at > QUEUE_TTL_MS) return;
    let ok = false;
    try {
      ok = send(item.payload) !== false;
    } catch (_) {
      ok = false;
    }
    if (ok) sent += 1;
    else pending.push(item);
  });
  return sent;
}

export const queuedCallSignalCount = () => pending.length;

// ── Event decisions ─────────────────────────────────────────────────────────────────────────
const IGNORE = { action: "ignore" };
const LEGACY = { action: "legacy" };

const chatDiffers = (event, chatId) =>
  Boolean(event && event.chat_id && chatId && String(event.chat_id) !== String(chatId));

/**
 * How this tab's call UI reacts to a call event. `view` = { callState, incoming, activeChatId }.
 * Returns { action } with action:
 *   'ignore'           - foreign, stale or duplicate: do nothing
 *   'legacy'           - run the existing (pre-instance) handler unchanged
 *   'ringing'          - outgoing attempt confirmed ringing (start the heartbeat)
 *   'connected'        - outgoing attempt answered (state sync / re-ack)
 *   'heartbeat'        - live call confirmed after a reconnect: make sure the heartbeat runs
 *   'end'              - end the live call locally; `notice` is the user message ('' for none)
 *   'dismiss_incoming' - dismiss the ringing incoming call (answered on another device); local
 *                        only, the live call must NOT be ended
 *   'leave_local'      - this device lost a simultaneous answer: leave the media session locally;
 *                        never call_end and never end the meeting for everyone (F-06)
 * Side effect: adopts the instance id from a matching ack and records applied sync versions.
 */
export function decideCallEvent(event, view = {}) {
  const ev = event && (event.event || event.type);
  const callState = view.callState;
  const incoming = view.incoming || null;
  const ringingIncoming = Boolean(incoming) && (callState === "incoming" || callState === undefined);

  if (ev === "call_offer_ack") {
    const ack = interpretOfferAck(event, identity);
    if (!ack) return IGNORE;
    adoptCallInstance(ack.callInstanceId, ack.ringDeadlineAt);
    if (callState !== "calling") return ack.outcome === "answered" ? { action: "connected" } : IGNORE;
    if (ack.outcome === "ringing") return { action: "ringing" };
    if (ack.outcome === "answered") return { action: "connected" };
    if (ack.outcome === "busy") return { action: "end", notice: terminalNotice("BUSY") };
    return { action: "end", notice: "" };
  }

  if (ev === "call_state_sync") {
    const sync = interpretStateSync(event, identity);
    if (sync.apply) {
      // A sync matched by client_call_id (the ack was lost, e.g. across a reconnect) also
      // carries the authoritative instance: adopt it so later events / call_end are bound.
      identity = {
        ...identity,
        callInstanceId: identity.callInstanceId || String(event.call_instance_id),
        ringDeadlineAt: identity.ringDeadlineAt || toDeadline(event.ring_deadline_at),
        version: sync.version,
      };
      if (sync.terminal) {
        return LIVE_STATES.includes(callState) ? { action: "end", notice: terminalNotice(sync.state) } : IGNORE;
      }
      if (sync.state === "ANSWERED" && callState === "calling") return { action: "connected" };
      if (LIVE_STATES.includes(callState) && !isCallHeartbeatRunning()) return { action: "heartbeat" };
      return IGNORE;
    }
    // The ringing incoming call (instance known) was resolved before this tab acted on it.
    if (
      ringingIncoming &&
      incoming.call_instance_id &&
      String(event.call_instance_id) === String(incoming.call_instance_id)
    ) {
      const state = String(event.state);
      // Cancelled / missed / ended: same handling as the legacy call_end that follows it.
      if (TERMINAL_STATES.includes(state)) return { action: "end", notice: "" };
      // Answered on another device of this user: dismiss locally, never end the live call.
      if (state === "ANSWERED") return { action: "dismiss_incoming" };
    }
    return IGNORE;
  }

  if (ev === "call_answered_elsewhere") {
    if (!ringingIncoming || isForOtherIncoming(event, incoming) || chatDiffers(event, incoming.chat_id)) return IGNORE;
    return { action: "dismiss_incoming" };
  }

  if (ev === "call_answer") {
    if (isForOtherAttempt(event, identity) || chatDiffers(event, view.activeChatId)) return IGNORE;
    return LEGACY;
  }

  if (ev === "call_end" && String(event.reason || "") === "answered_elsewhere") {
    // F-06: this device's answer lost to another device of the same user. The call continues
    // there: leave locally only (never call_end, never end the meeting for everyone).
    const ci = event.call_instance_id ? String(event.call_instance_id) : null;
    if (ci && identity.callInstanceId && ci === String(identity.callInstanceId) && LIVE_STATES.includes(callState)) {
      return { action: "leave_local" };
    }
    if (ringingIncoming && ci && incoming.call_instance_id && ci === String(incoming.call_instance_id)) {
      return { action: "dismiss_incoming" };
    }
    return IGNORE;
  }

  if (ev === "call_reject" || ev === "call_busy" || ev === "call_end") {
    if (ringingIncoming) {
      return isForOtherIncoming(event, incoming) || chatDiffers(event, incoming.chat_id) ? IGNORE : LEGACY;
    }
    if (isForOtherAttempt(event, identity) || chatDiffers(event, view.activeChatId)) return IGNORE;
    return LEGACY;
  }

  return LEGACY;
}

/**
 * A `call_offer` while a call from the same caller is already ringing: 'supersede' when it is a
 * NEWER instance (the backend replaced the ringing one), 'duplicate' for a re-delivery, else null.
 */
export function decideRepeatOffer(offer, incoming) {
  if (!offer || !incoming || String(incoming.from_user_id) !== String(offer.from_user_id)) return null;
  return isForOtherIncoming(offer, incoming) ? "supersede" : "duplicate";
}

/** Test hook: reset all per-tab state. */
export function __resetCallIdentityForTests() {
  resetCallIdentity();
  pending = [];
}
